import { format } from 'date-fns';
import { type ReactNode, useMemo } from 'react';

import { Avatar } from '@air/primitive-avatar';
import { formatFullName, parseAirDateToISO } from '@air/utilities';

import { RecentlyDeletedTableCell } from '~/components/RecentlyDeleted/components/RecentlyDeletedTableCell';
import { RecentlyDeletedTableCellAction } from '~/components/RecentlyDeleted/components/RecentlyDeletedTableCellAction';
import { RecentlyDeletedTableCellDaysToRecover } from '~/components/RecentlyDeleted/components/RecentlyDeletedTableCellDaysToRecover';
import { RecentlyDeletedTableCellDeletedFrom } from '~/components/RecentlyDeleted/components/RecentlyDeletedTableCellDeletedFrom';
import { RecentlyDeletedTableCellTitle } from '~/components/RecentlyDeleted/components/RecentlyDeletedTableCellTitle';
import { RecentlyDeletedTableCellType } from '~/components/RecentlyDeleted/components/RecentlyDeletedTableCellType';
import { MIN_TABLE_CONTENT_WIDTH } from '~/components/RecentlyDeleted/constants/tableWidth';
import { type RecentlyDeletedItemType, type RecentlyDeletedResponse } from '~/components/RecentlyDeleted/types';
import { getDeletedByAvatarProps } from '~/components/RecentlyDeleted/utils/getDeletedByAvatarProps';
import { getTableColumnWidth } from '~/components/RecentlyDeleted/utils/getTableColumnWidth';
import { TableRow, type TableRowProps } from '~/components/TableView/TableRow/TableRow';

export type RecentlyDeletedTableRowProps = Pick<TableRowProps, 'style'> & {
  actions?: ReactNode;
  dateDeleted: string;
  deletedFrom?: { title: string; id: string }[];
  recoveryWindow: number;
  deletedBy: RecentlyDeletedResponse['data'][number]['deletedBy'];
  thumbnail: ReactNode;
  title: string;
  meta?: ReactNode;
  type: RecentlyDeletedItemType;
};

export const RecentlyDeletedTableRow = ({
  actions,
  deletedBy,
  dateDeleted,
  deletedFrom,
  recoveryWindow,
  thumbnail,
  title,
  meta,
  style,
  type,
}: RecentlyDeletedTableRowProps) => {
  const actorAvatar = useMemo(() => <Avatar {...getDeletedByAvatarProps(deletedBy)} />, [deletedBy]);
  const deletedByFullName = useMemo(() => formatFullName(deletedBy.firstName, deletedBy.lastName), [deletedBy]);

  return (
    <div className="pr-4 pl-4" style={{ ...style, minWidth: MIN_TABLE_CONTENT_WIDTH }}>
      <TableRow className="group/table-row rounded-4 hover:bg-gray-3">
        <RecentlyDeletedTableCellTitle thumbnail={thumbnail} title={title} meta={meta} />
        <RecentlyDeletedTableCellType type={type} />
        <RecentlyDeletedTableCell width={getTableColumnWidth('dateDeleted')}>
          {format(parseAirDateToISO(dateDeleted), 'MMM dd, yyyy')}
        </RecentlyDeletedTableCell>
        <RecentlyDeletedTableCell width={getTableColumnWidth('deletedBy')}>
          <div className="flex w-full items-center gap-2">
            {actorAvatar}
            <span className="truncate">{deletedByFullName}</span>
          </div>
        </RecentlyDeletedTableCell>
        <RecentlyDeletedTableCellDeletedFrom deletedFrom={deletedFrom} />
        <RecentlyDeletedTableCellDaysToRecover recoveryWindow={recoveryWindow} dateDeleted={dateDeleted} />
        <RecentlyDeletedTableCellAction>{actions}</RecentlyDeletedTableCellAction>
      </TableRow>
    </div>
  );
};
